'use client';

import * as React from 'react';
import { Popover } from '@base-ui/react/popover';
import { useTypeProp } from '@mui/internal-docs-infra/useType';
import type { TypePropRefProps } from '@mui/internal-docs-infra/useType';
import { PopoverArrow } from '@/components/PopoverArrow/PopoverArrow';
import {
  getHrefTargetId,
  getTypeSectionId,
  useCurrentTypesTableId,
} from '@/components/TypesTableContext';
import styles from './TypePropRef.module.css';

/**
 * Renders a type property reference as an interactive element.
 * When this is the definition site (`id` is present), renders a span with the anchor id.
 * When property metadata is available, shows a popover with description, type, and default.
 * At the definition site the popover omits the type (already shown inline).
 *
 * Falls back to a plain span or anchor when no property data is available.
 */
export function TypePropRef({ id, href, name, prop, className, children }: TypePropRefProps) {
  const currentTypesTableId = useCurrentTypesTableId();
  const propData = useTypeProp(name, prop);
  const property = propData?.property;
  const resolvedHref = propData?.href ?? href;
  const targetId = getHrefTargetId(resolvedHref);
  const targetTypeSectionId = getTypeSectionId(targetId);
  const isCurrentTypesTableLink = Boolean(
    !id && currentTypesTableId && targetTypeSectionId === currentTypesTableId,
  );

  const hasPopoverContent =
    property &&
    (property.description || property.default || property.example || property.detailedType);

  if (isCurrentTypesTableLink && resolvedHref) {
    return (
      <a href={resolvedHref} className={[className, styles.link].filter(Boolean).join(' ')}>
        {children}
      </a>
    );
  }

  // Definition site with popover content: show anchor span + popover for meta
  if (id && hasPopoverContent) {
    return (
      <Popover.Root>
        <Popover.Trigger id={id} className={[className, styles.trigger].filter(Boolean).join(' ')}>
          {children}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner sideOffset={8}>
            <Popover.Popup className={styles.popup}>
              <PopoverArrow />
              <div className={styles.content}>
                <div className={styles.scrollArea}>
                  <PropPopoverContent property={property} />
                </div>
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    );
  }

  // Definition site without popover content: plain span with anchor
  if (id) {
    return (
      <span id={id} className={className}>
        {children}
      </span>
    );
  }

  // Reference site with popover content
  if (hasPopoverContent) {
    return (
      <Popover.Root>
        <Popover.Trigger className={[className, styles.trigger].filter(Boolean).join(' ')}>
          {children}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner sideOffset={8}>
            <Popover.Popup className={styles.popup}>
              <PopoverArrow />
              <div className={styles.content}>
                <div className={styles.scrollArea}>
                  {resolvedHref && (
                    <div className={styles.header}>
                      <a href={resolvedHref} className={styles.headerLink}>
                        Go to definition
                      </a>
                      <Popover.Close aria-label="Close" className={styles.close}>
                        &times;
                      </Popover.Close>
                    </div>
                  )}
                  <PropPopoverContent property={property} showType />
                </div>
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    );
  }

  // No popover: fall back to link or plain span
  if (!resolvedHref) {
    return <span className={className}>{children}</span>;
  }

  return (
    <a href={resolvedHref} className={[className, styles.link].filter(Boolean).join(' ')}>
      {children}
    </a>
  );
}

function PropPopoverContent({
  property,
  showType,
}: {
  property: NonNullable<ReturnType<typeof useTypeProp>>['property'];
  showType?: boolean;
}) {
  return (
    <dl className={styles.propList}>
      {property.description && (
        <div className={styles.propRow}>
          <dd className={styles.description}>{property.description}</dd>
        </div>
      )}
      {showType && (
        <div className={styles.propRow}>
          <dt className={styles.label}>Type</dt>
          <dd className={styles.value}>{property.detailedType ?? property.type}</dd>
        </div>
      )}
      {property.default && (
        <div className={styles.propRow}>
          <dt className={styles.label}>Default</dt>
          <dd className={styles.value}>{property.default}</dd>
        </div>
      )}
      {property.example && (
        <div className={styles.propRow}>
          <dt className={styles.label}>Example</dt>
          <dd className={styles.value}>{property.example}</dd>
        </div>
      )}
    </dl>
  );
}
